/*
 * Native video, the Tolstoy replacement (session A5b).
 *
 * <fz-video-player>  tap to play WITH sound, tap to pause, a mute toggle and a
 *                    progress bar. Never autoplays. One plays at a time; one
 *                    that leaves the screen pauses; one that ends returns to
 *                    its poster.
 * <fz-video-carousel> the swipeable row: native horizontal scroll with CSS
 *                    scroll-snap (momentum and rubber-banding are the
 *                    browser's own, so a swipe feels like the phone), plus
 *                    previous/next buttons for a mouse and a keyboard, which
 *                    step one card at a time and say when they are at an end.
 *
 * INP: every handler does a constant amount of work (set an attribute, call
 * play() or pause(), or one scrollBy); nothing measures layout in a handler
 * except the carousel's single getBoundingClientRect on a button press. The
 * progress bar is a transform written from requestAnimationFrame only while a
 * clip plays.
 */

const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
let current = null;

/*
 * POSTERS LOAD WHEN THEIR CARD IS NEAR, not when the browser's lazy loading
 * decides. The markup carries a 32 px placeholder and the real srcset in
 * data-fz-srcset (see snippets/fz-video-player.liquid for the measurement).
 * Setting srcset keeps the placeholder on screen until the real picture has
 * decoded, so there is no blank frame. A card clipped by the carousel's own
 * scroll never intersects, so the carousel hydrates the rest of its row in
 * idle time once its first poster has come near.
 */
function hydrate(img) {
  if (!img || !img.dataset.fzSrcset) return;
  img.sizes = img.dataset.fzSizes || "";
  img.srcset = img.dataset.fzSrcset;
  delete img.dataset.fzSrcset;
  delete img.dataset.fzSizes;
  img.closest("fz-video-carousel")?.dispatchEvent(new Event("fz:poster"));
}

/*
 * AND ONCE THE HERO PHOTO HAS LOADED, the posters a shopper would see first
 * load too, near or not: each carousel's visible cards, and every card of a
 * grid. A visitor on a desktop who does not scroll, and a capture that never
 * does (the four-width review shots, visual sweep V-018), otherwise sees the
 * 32 px placeholders, blurred.
 * The trigger is the hero photo, the page's LCP, and NOT the window's `load`
 * event, which it used to be (with an idle callback on top). `load` waits for
 * every iframe and third-party script on this page, the Loox wall among them,
 * so it came after check V's capture at 1280 and 1440 (A6's run, 26 Sep) and
 * after a real visitor had long been looking at blurred cards. Once the hero
 * has painted, a few posters cost the LCP nothing. With no hero on the page
 * (the TV page), or if it has already loaded, they load at once. `load` stays
 * as a fallback, and the swap runs only once.
 */
function hydrateVisibleAfterHero() {
  let done = false;
  const run = () => {
    if (done) return;
    done = true;
    for (const carousel of document.querySelectorAll("fz-video-carousel")) {
      const track = carousel.querySelector("[data-fz-track]");
      const grid = !carousel.closest(".fz-videos--carousel");
      const edge = track ? track.getBoundingClientRect().right : Infinity;
      for (const img of carousel.querySelectorAll(".fz-video__poster[data-fz-srcset]")) {
        const card = img.closest("[role='listitem']") || img;
        if (grid || card.getBoundingClientRect().left < edge) hydrate(img);
      }
    }
  };
  const soon = () => requestAnimationFrame(run);
  const hero = document.querySelector(".fz-hero__image");
  if (!hero || (hero.complete && hero.naturalWidth > 0)) {
    soon();
    return;
  }
  hero.addEventListener("load", soon, { once: true });
  hero.addEventListener("error", soon, { once: true });
  window.addEventListener("load", soon, { once: true });
}
hydrateVisibleAfterHero();

const near =
  "IntersectionObserver" in window
    ? new IntersectionObserver(
        (entries, observer) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            observer.unobserve(entry.target);
            hydrate(entry.target);
          }
        },
        { rootMargin: "50% 0px" },
      )
    : null;

class FzVideoPlayer extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector("video");
    this.toggle = this.querySelector(".fz-video__toggle");
    this.mute = this.querySelector(".fz-video__mute");
    this.bar = this.querySelector(".fz-video__progress-bar");
    this.poster = this.querySelector(".fz-video__poster[data-fz-srcset]");
    if (this.poster) near ? near.observe(this.poster) : hydrate(this.poster);
    if (!this.video || !this.toggle) return;

    this.onToggle = () => (this.video.paused ? this.play() : this.pause());
    this.onMute = () => this.setMuted(!this.video.muted);
    this.toggle.addEventListener("click", this.onToggle);
    this.mute?.addEventListener("click", this.onMute);

    this.video.addEventListener("playing", () => this.setState("playing"));
    this.video.addEventListener("waiting", () => {
      if (!this.video.paused) this.setState("loading");
    });
    this.video.addEventListener("pause", () => {
      if (!this.video.ended) this.setState("paused");
    });
    this.video.addEventListener("ended", () => this.reset());
    // The mute button follows the element, not the intent: when an app
    // refuses sound even on a tap, play()'s catch mutes the clip after
    // syncMute() already ran, and the button said "sound on" over a silent
    // clip (A7 in-app risk IA-10). volumechange fires on every muted change.
    this.video.addEventListener("volumechange", () => this.syncMute());
    this.video.addEventListener("error", () => this.reset());

    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (entry && !entry.isIntersecting && !this.video.paused) this.pause();
      },
      { threshold: 0.35 },
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    if (this.poster) near?.unobserve(this.poster);
    this.toggle?.removeEventListener("click", this.onToggle);
    this.mute?.removeEventListener("click", this.onMute);
    cancelAnimationFrame(this.frame);
  }

  play() {
    hydrate(this.poster);
    if (current && current !== this) current.pause();
    current = this;
    this.setState("loading");
    this.video.muted = false;
    if (this.video.preload !== "auto") this.video.preload = "auto";
    const attempt = this.video.play();
    if (attempt && attempt.catch) {
      attempt.catch(() => {
        // A browser that refuses sound even on a tap (some in-app webviews)
        // gets the clip muted rather than nothing; the mute button says so.
        this.video.muted = true;
        this.video.play().catch(() => this.reset());
      });
    }
    this.toggle.setAttribute("aria-pressed", "true");
    this.toggle.setAttribute("aria-label", this.toggle.dataset.labelPause);
    if (this.mute) {
      this.mute.hidden = false;
      this.syncMute();
    }
    this.tick();
  }

  pause() {
    this.video.pause();
    this.toggle.setAttribute("aria-pressed", "false");
    this.toggle.setAttribute("aria-label", this.toggle.dataset.labelPlay);
    cancelAnimationFrame(this.frame);
  }

  reset() {
    this.pause();
    try {
      this.video.currentTime = 0;
    } catch (e) {
      /* not seekable yet */
    }
    if (this.bar) this.bar.style.transform = "scaleX(0)";
    if (this.mute) this.mute.hidden = true;
    this.setState("idle");
    if (current === this) current = null;
  }

  setMuted(muted) {
    this.video.muted = muted;
    this.syncMute();
  }

  syncMute() {
    if (!this.mute) return;
    const muted = this.video.muted;
    this.mute.setAttribute("aria-pressed", String(muted));
    this.mute.setAttribute(
      "aria-label",
      muted ? this.mute.dataset.labelUnmute || "Unmute" : this.mute.dataset.labelMute || "Mute",
    );
  }

  setState(state) {
    this.dataset.state = state;
  }

  tick() {
    cancelAnimationFrame(this.frame);
    const step = () => {
      const { currentTime, duration } = this.video;
      if (this.bar && duration > 0) this.bar.style.transform = `scaleX(${Math.min(currentTime / duration, 1)})`;
      if (!this.video.paused && !this.video.ended) this.frame = requestAnimationFrame(step);
    };
    this.frame = requestAnimationFrame(step);
  }
}

class FzVideoCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector("[data-fz-track]");
    this.prev = this.querySelector("[data-fz-prev]");
    this.next = this.querySelector("[data-fz-next]");
    if (!this.track) return;

    this.onPrev = () => this.step(-1);
    this.onNext = () => this.step(1);
    this.prev?.addEventListener("click", this.onPrev);
    this.next?.addEventListener("click", this.onNext);

    // A row's off-screen cards are clipped by the track, so they never come
    // "near" on their own: once the first poster has, fetch the rest when the
    // browser is idle. A grid (the TV page) is not clipped and needs none of it.
    if (this.closest(".fz-videos--carousel")) {
      this.addEventListener(
        "fz:poster",
        () => {
          const rest = () => this.querySelectorAll(".fz-video__poster[data-fz-srcset]").forEach(hydrate);
          "requestIdleCallback" in window ? requestIdleCallback(rest, { timeout: 2000 }) : setTimeout(rest, 200);
        },
        { once: true },
      );
    }

    // The buttons are disabled at the ends. Two sentinels, one per end, say so
    // without a scroll listener.
    const first = this.track.firstElementChild;
    const last = this.track.lastElementChild;
    if (first && last && this.prev && this.next) {
      this.edges = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const button = entry.target === first ? this.prev : this.next;
            button.disabled = entry.intersectionRatio > 0.9;
          }
        },
        { root: this.track, threshold: [0, 0.9, 1] },
      );
      this.edges.observe(first);
      this.edges.observe(last);
    }
  }

  disconnectedCallback() {
    this.edges?.disconnect();
    this.prev?.removeEventListener("click", this.onPrev);
    this.next?.removeEventListener("click", this.onNext);
  }

  step(direction) {
    const card = this.track.firstElementChild;
    if (!card) return;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const width = card.getBoundingClientRect().width + gap;
    const visible = Math.max(1, Math.floor((this.track.clientWidth + gap) / width));
    const rtl = getComputedStyle(this).direction === "rtl" ? -1 : 1;
    this.track.scrollBy({
      left: direction * rtl * width * visible,
      behavior: reduceMotion.matches ? "auto" : "smooth",
    });
  }
}

if (!customElements.get("fz-video-player")) customElements.define("fz-video-player", FzVideoPlayer);
if (!customElements.get("fz-video-carousel")) customElements.define("fz-video-carousel", FzVideoCarousel);
